// Login-screen company pre-selection.
//
// The login screen lets the user pick which company (DCC/TTOP/RCI) to work in
// BEFORE authenticating — but at that point only the company *code* is known,
// not the per-deployment UUID that the active-company state is keyed by (the
// UUID only exists client-side once /companies loads post-auth). So we stash the
// chosen code in localStorage (survives the full-page Entra redirect) and
// resolve it to an allowed company id once user + companies are available — see
// `useApplyPendingLoginCompany` in lib/queries/companies.

import type { Company, CompanyCode } from '@dcc-erp/shared';

export const PENDING_LOGIN_COMPANY_KEY = 'dcc-erp/pending-login-company';

/** Stash the company code chosen on the login screen (call right before loginRedirect). */
export function savePendingLoginCompany(code: CompanyCode): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(PENDING_LOGIN_COMPANY_KEY, code);
  } catch {
    // localStorage unavailable (private mode) — degrade to the default company.
  }
}

/** Peek at the stashed code without consuming it (null when none/unavailable). */
export function readPendingLoginCompany(): string | null {
  if (typeof window === 'undefined') return null;
  try {
    return window.localStorage.getItem(PENDING_LOGIN_COMPANY_KEY);
  } catch {
    return null;
  }
}

/** Drop the stashed code (single-use after apply, or on a failed/abandoned login). */
export function clearPendingLoginCompany(): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.removeItem(PENDING_LOGIN_COMPANY_KEY);
  } catch {
    // ignore
  }
}

/**
 * Map a login-screen company code to the matching company UUID — but only when
 * the user is actually allowed that company. Returns null when the code is
 * empty/unknown, the companies list hasn't loaded, or the user isn't permitted
 * the company, so the caller falls back to the default active company and
 * `setActiveCompanyId` is never called with a disallowed id (which would throw).
 */
export function resolvePendingLoginCompanyId(
  pendingCode: string | null,
  companies: Company[],
  allowedCompanyIds: string[],
): string | null {
  if (!pendingCode) return null;
  const match = companies.find(
    (c) => c.code === pendingCode && c.id != null && allowedCompanyIds.includes(c.id),
  );
  return match?.id ?? null;
}
